import { ICustomProperties, IExceptionTelemetry, SeverityLevel } from '@microsoft/applicationinsights-web';
import { appInsights } from './applicationInsights';

// TYPES
export type CommonLogMetaData = {
  instance: string;
  processName: string;
  action: string;
  step: string;
} & Record<string, unknown>;

export type TraceLog = CommonLogMetaData;

type CustomErrorLogProperties = CommonLogMetaData & {
  locale?: string;
  slug?: string;
};

export type CorporateLogProperties = CustomErrorLogProperties & {
  component?: string;
  digest?: string;
};

export type SanityErrorLogProperties = CustomErrorLogProperties & {
  sanityDocumentId?: string;
  sanityDocumentType?: string;
  type?: string;
  message?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  consoleErrorArgs?: { index?: number; type: string; value: Parameters<typeof console.error>[number] }[];
};

const isDev = () => process.env.NODE_ENV !== 'production';

const getDefaultApplicationName = () =>
  process.env.APP_NAME || 'Unknown Web Application';

const buildCommonCustomProperties = (meta?: CommonLogMetaData): ICustomProperties => {
  return {
    ...meta,
    applicationName: getDefaultApplicationName(),
  };
};

export const buildMessageWithMeta = (meta: CommonLogMetaData): string => {
  return `${meta.instance}:${meta.processName}:${meta.action}:${meta.step}`;
}

// We want to see messages directly when not in production mode
/* eslint-disable no-console */

export function logInfoMessage(traceLog: TraceLog) {
  const customProperties: ICustomProperties = buildCommonCustomProperties(traceLog);

  appInsights?.trackTrace(
    {
      message: buildMessageWithMeta(traceLog),
      severityLevel: SeverityLevel.Information,
    },
    customProperties,
  );

  if (isDev()) console.log(traceLog);
}

export function logWarningMessage(traceLog: TraceLog, ) {
  const customProperties: ICustomProperties = buildCommonCustomProperties(traceLog);

  appInsights?.trackTrace(
    {
      message: buildMessageWithMeta(traceLog),
      severityLevel: SeverityLevel.Warning,
    },
    customProperties,
  );

  if (isDev()) console.warn(traceLog);
}

export function logErrorMessage(
  error: Error,
  properties?: CorporateLogProperties | SanityErrorLogProperties,
  skipConsole?: boolean,
) {
  const exception: IExceptionTelemetry = {
    exception: error,
    severityLevel: SeverityLevel.Error,
  };

  const customProperties: ICustomProperties = {
    ...properties,
    ...buildCommonCustomProperties(properties),
  };

  appInsights?.trackException(exception, customProperties);
  if (!skipConsole && isDev()) console.error(exception, customProperties);
}
